import { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FOR_BANK_SECTIONS, FOR_BANK_SUB_SECTIONS } from 'constants/businessToolSections/forBank';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import { FieldArrayComponent } from 'components/_basic/Form/FieldArray';
import { TextInputControl } from 'components/_basic/Form/TextInput';
import { FormHeader } from 'components/_basic/FormHeader/FormHeader';
import { FormSectionCard } from 'components/_basic/FormSectionCard/FormSectionCard';
import { FORM_SECTION_CARD_VARIANT } from 'components/_basic/FormSectionCard/types';

import { concatWithDot } from 'helpers/string';

import { ABOUT_COMPANY_MISSION_NAMES, goalAndObjectiveRowInitialValues, prefix } from './utils/form';
import { GoalAndObjectiveRow } from '../../ExecutiveSummary/GoalsExecSummary/components/GoalAndObjectiveRow';
import { GLOBAL_FIELD_NAMES, globalPrefix } from '../../Global/form';

export const MissionAndVision: FC = () => {
  const { t } = useTranslation();
  const { control } = useFormContext();

  const canAccess = useCheckSubSectionAccess(
    FOR_BANK_SECTIONS.ABOUT_COMPANY,
    FOR_BANK_SUB_SECTIONS.FOR_BANK_ABOUT_COMPANY.MISSION_AND_STRATEGY,
  );

  return (
    <>
      <FormSectionCard variant={FORM_SECTION_CARD_VARIANT.LARGE}>
        <FormHeader
          locked={!canAccess}
          title={t('company.title')}
          badgeText={t('company.mission.title')}
          section={FOR_BANK_SECTIONS.ABOUT_COMPANY}
        />
      </FormSectionCard>
      <FormSectionCard
        disabled={!canAccess}
        title={t('company.mission.missionStatement')}
        aiFieldName={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.MISSION)}
      >
        <TextInputControl
          multiline
          control={control}
          id="company.mission.missionStatement"
          name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.MISSION)}
          minRows={4}
          disabled={!canAccess}
        />
      </FormSectionCard>
      <FormSectionCard
        disabled={!canAccess}
        title={t('company.mission.visionOfFuture')}
        aiFieldName={concatWithDot(prefix, ABOUT_COMPANY_MISSION_NAMES.VISION_OF_FUTURE)}
      >
        <TextInputControl
          multiline
          control={control}
          id="company.mission.visionOfFuture"
          name={concatWithDot(prefix, ABOUT_COMPANY_MISSION_NAMES.VISION_OF_FUTURE)}
          minRows={4}
          disabled={!canAccess}
        />
      </FormSectionCard>
      <FormSectionCard
        disabled={!canAccess}
        title={t('company.mission.coreValues')}
        aiFieldName={concatWithDot(prefix, ABOUT_COMPANY_MISSION_NAMES.CORE_VALUES)}
      >
        <TextInputControl
          multiline
          control={control}
          id="company.mission.coreValues"
          name={concatWithDot(prefix, ABOUT_COMPANY_MISSION_NAMES.CORE_VALUES)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      <FormSectionCard
        disabled={!canAccess}
        title={t('executiveSummary.goalsAndObjectives.mainGoals')}
        collapsible
        aiFieldName={concatWithDot(prefix, ABOUT_COMPANY_MISSION_NAMES.GOALS)}
        aiFieldType="complex"
      >
        <FieldArrayComponent
          name={concatWithDot(prefix, ABOUT_COMPANY_MISSION_NAMES.GOALS)}
          initialItem={goalAndObjectiveRowInitialValues}
          buttonText={t('goal')}
          disabled={!canAccess}
        >
          {(namePrefix) => <GoalAndObjectiveRow name={namePrefix} disabled={!canAccess} />}
        </FieldArrayComponent>
      </FormSectionCard>
      <FormSectionCard
        disabled={!canAccess}
        title={t('company.mission.problemOfMarket')}
        aiFieldName={concatWithDot(prefix, ABOUT_COMPANY_MISSION_NAMES.PROBLEM_OF_MARKET)}
      >
        <TextInputControl
          multiline
          control={control}
          id="company.mission.problemOfMarket"
          name={concatWithDot(prefix, ABOUT_COMPANY_MISSION_NAMES.PROBLEM_OF_MARKET)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      <FormSectionCard
        disabled={!canAccess}
        title={t('company.mission.howProjectAddressesTheProblem')}
        aiFieldName={concatWithDot(prefix, ABOUT_COMPANY_MISSION_NAMES.HOW_PROJECT_ADDRESSES_THE_PROBLEM)}
      >
        <TextInputControl
          multiline
          control={control}
          id="company.mission.howProjectAddressesTheProblem"
          name={concatWithDot(prefix, ABOUT_COMPANY_MISSION_NAMES.HOW_PROJECT_ADDRESSES_THE_PROBLEM)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
    </>
  );
};
